'use client'

import { memo, useMemo, type ComponentType } from 'react'
import { useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'

export type MarketingContentTile = {
  id: string
  title: string
  subtitle?: string
  icon?: ComponentType<{ className?: string }>
}

export type MarketingContentMarqueeProps = {
  tiles: readonly MarketingContentTile[]
  label?: string
  className?: string
}

const Tile = memo(function Tile({
  tile,
  index,
}: {
  tile: MarketingContentTile
  index: number
}) {
  const Icon = tile.icon
  // baby: Alternate slight tilts so the strip feels floated, not flat
  const tilt = index % 2 === 0 ? -4 : 3

  return (
    <div
      className="marketing-content-tile"
      style={{ transform: `rotate(${tilt}deg)` }}
    >
      {Icon ? (
        <Icon className="mb-2 h-5 w-5 text-[color:var(--ninxl-brand-teal,#21728d)]" />
      ) : null}
      <div className="ninxl-8bit-heading text-sm tracking-[0.06em] text-[color:var(--ninxl-theme-fg)]">
        {tile.title}
      </div>
      {tile.subtitle ? (
        <p className="mt-1 text-xs leading-snug text-[color:var(--ninxl-8bit-muted)]">
          {tile.subtitle}
        </p>
      ) : null}
    </div>
  )
})

const MarqueeRow = memo(function MarqueeRow({
  tiles,
  reverse,
  reduced,
}: {
  tiles: readonly MarketingContentTile[]
  reverse?: boolean
  reduced: boolean
}) {
  const loop = useMemo(
    () => (reduced ? [...tiles] : [...tiles, ...tiles]),
    [tiles, reduced],
  )

  return (
    <div className="ninxl-marquee-mask relative overflow-hidden py-2">
      <div
        className={cn(
          'flex w-max items-stretch gap-4 md:gap-5',
          reduced
            ? 'flex-wrap justify-center'
            : reverse
              ? 'ninxl-marquee-reverse'
              : 'ninxl-marquee-forward',
        )}
      >
        {loop.map((tile, index) => (
          <Tile key={`${tile.id}-${index}`} tile={tile} index={index} />
        ))}
      </div>
    </div>
  )
})

/**
 * baby: Webflow pay angled content-block marquee: restyled as 8-bit tiles
 * (not Ninjapay finance copy).
 */
export const MarketingContentMarquee = memo(function MarketingContentMarquee({
  tiles,
  label = 'Capabilities',
  className,
}: MarketingContentMarqueeProps) {
  const reduced = Boolean(useReducedMotion())

  if (tiles.length === 0) {
    return null
  }

  return (
    <section
      className={cn('marketing-content-marquee overflow-hidden py-6', className)}
      aria-label={label}
      data-section="content-marquee"
    >
      <div className={cn(!reduced && 'marketing-content-marquee-angle')}>
        <MarqueeRow tiles={tiles} reduced={reduced} />
        <MarqueeRow tiles={tiles} reverse reduced={reduced} />
      </div>
    </section>
  )
})
